import { Button } from "@blurb/codex-react";
import { MantineProvider } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";

import { Pagination } from "../../components/Pagination";
import { Heading } from "../../components/Typography";
import { usePageShellStyles } from "../../components/pageShellStyles";
import { SearchBar, SearchFilterBar } from "../../components/search-filter/SearchFilterBar";
import { SearchInput } from "../../configuration/filters/searchInput";
import { featureFlags } from "../../featureFlags";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useCustomerProducts } from "../../hooks/useCustomerProducts";
import { useDrawerRoute } from "../../hooks/useDrawerRoute";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../hooks/usePagination";
import { rpaTheme } from "../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../utils/graphql.util";
import { customerProductSearchLabels } from "../../utils/searchField.util";
import { PricingCalculator } from "../index/PricingCalculator";
import { CustomerProductCreateView, path as createPath } from "./CustomerProductCreateView";
import { CustomerProductFilter } from "./CustomerProductFilter";
import { CustomerProductsTable } from "./CustomerProductsTable";
import { CustomerProductViewV2 } from "./CustomerProductView";
import { CustomerProductEditViewV2 } from "./CustomerProductEditViewV2";

export const CustomerProductsView: React.FC = () => {
  const { classes } = usePageShellStyles();
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const createDrawerRoute = useDrawerRoute(createPath);
  const detailsDrawerRoute = useDrawerRoute("", /customer-products\/\d+\/?$/);
  const editDrawerRoute = useDrawerRoute("", /\d+\/edit\/?$/);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [isPriceCalculatorOpen, setIsPriceCalculatorOpen] = React.useState(false);

  const sortInput: SearchSort<GraphQL.CustomerProductsQueryVariables> = {
    by: GraphQL.CustomerProductSearchField.Id,
    sortDirection: GraphQL.SortDirection.Desc,
  };
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.CustomerProductSearchField>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.CustomerProductsQueryVariables>>();

  const pagination = useRpiPagination({ perPage: 20 });

  const { data, error, isLoading, refetch } = useCustomerProducts(user.is.SUPER_ADMIN, pagination, {
    inputs: searchInputs,
    sort: sortInput,
    generalInput: searchGeneral,
  });

  const customerProducts = data?.customerProducts.data;

  React.useEffect(() => {
    if (outlet === null) {
      refetch();
    }
  }, [outlet]);

  const [productId, setProductId] = React.useState<number | undefined>(undefined);

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
  };

  // Below 1200px the controls row can't fit search + Filters + Catalog + Add Product inline; stack CTAs
  // into the title row. Defaults to wide so SSR and Playwright's 1280px viewport both render inline.
  const isWide = useMediaQuery("(min-width: 1200px)", true);

  const ctaButtons = (
    <div className={classes.titleActions}>
      {user.is.SUPER_ADMIN && (
        <Button variant="outlined" intent="primary" onClick={() => navigateRoute(["open-api-products"])}>
          <IconV3 icon="format_list_bulleted" color="inherit" size={20} />
          Catalog
        </Button>
      )}
      <Button variant="filled" intent="primary" onClick={createDrawerRoute.open} data-testid="addNewProductButton">
        <IconV3 icon="add" color="inherit" />
        Add New Product
      </Button>
    </div>
  );

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="products-view" aria-label="Products view">
        <div className={classes.titleRow}>
          <Heading as="h1" size="6xl">
            Products
          </Heading>
          {!isWide && ctaButtons}
        </div>
        <div className={classes.scrollStack}>
          <div className={classes.row}>
            <div className={classes.searchSlot}>
              {user.is.SUPER_ADMIN ? (
                <SearchFilterBar
                  placeholder="Search by Product Name or SKU"
                  isLoading={isLoading}
                  filters={searchInputs}
                  onSetFilters={setSearchInputs}
                  onSearchSubmit={handleSearchSubmit}
                  onFilterClick={() => setIsFilterOpen(true)}
                  filterLabels={customerProductSearchLabels}
                />
              ) : (
                <SearchBar
                  placeholder="Search by Product Name or SKU"
                  isLoading={isLoading}
                  onSearchSubmit={handleSearchSubmit}
                />
              )}
            </div>
            {isWide && ctaButtons}
          </div>
          <CustomerProductsTable
            customerProducts={customerProducts}
            isLoading={isLoading}
            error={error}
            setIsPriceCalculatorOpen={setIsPriceCalculatorOpen}
            setProductId={setProductId}
            footer={<Pagination isLoading={isLoading} pagination={pagination} />}
          />
        </div>
      </div>

      <CustomerProductCreateView isOpened={createDrawerRoute.isOpen} onClose={createDrawerRoute.close} />
      <PricingCalculator
        isOpened={isPriceCalculatorOpen}
        onClose={() => setIsPriceCalculatorOpen(false)}
        productId={productId}
        customerProducts={customerProducts}
      />
      {user.is.SUPER_ADMIN && (
        <CustomerProductFilter
          isOpened={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={searchInputs}
          onApply={setSearchInputs}
        />
      )}
      {featureFlags.newProductDetails ? (
        <>
          <CustomerProductViewV2 isOpened={detailsDrawerRoute.isOpen} onClose={detailsDrawerRoute.close} />
          <CustomerProductEditViewV2 isOpened={editDrawerRoute.isOpen} onClose={editDrawerRoute.close} />
        </>
      ) : (
        <>
          {detailsDrawerRoute.isOpen && outlet}
          {editDrawerRoute.isOpen && outlet}
        </>
      )}
    </MantineProvider>
  );
};
